import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { Bot, Send, X as XIcon, Loader2, Sparkles, CalendarClock, FileText, Users, Tag as TagIcon, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const SOURCE_META = {
  "agenda": { icon: CalendarClock, cls: "bg-sky-100 text-sky-800" },
  "cartella clinica": { icon: FileText, cls: "bg-emerald-100 text-emerald-800" },
  "pazienti": { icon: Users, cls: "bg-indigo-100 text-indigo-800" },
  "tag paziente": { icon: TagIcon, cls: "bg-amber-100 text-amber-800" },
  "consensi": { icon: ShieldCheck, cls: "bg-rose-100 text-rose-800" },
};

const SUGGESTIONS = [
  "Recuperami l'ultima visita del paziente…",
  "Quante persone ho in agenda a Napoli il 2 settembre 2026?",
  "Che giorno devo stare a Milano?",
  "Cerca pazienti con tag pterigio",
  "Chi non ha il consenso firmato?",
];

function SourceBadge({ source }) {
  if (!source) return null;
  const m = SOURCE_META[source] || { icon: Sparkles, cls: "bg-slate-100 text-slate-600" };
  const I = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${m.cls}`} data-testid="assistant-source-badge">
      <I size={10} /> {source}
    </span>
  );
}

export default function AssistantPanel() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [usedActionIds, setUsedActionIds] = useState(() => new Set());
  const [sessionId] = useState(() => {
    try {
      let s = window.sessionStorage.getItem("pv-assistant-session");
      if (!s) { s = crypto.randomUUID(); window.sessionStorage.setItem("pv-assistant-session", s); }
      return s;
    } catch { return `${Date.now()}`; }
  });
  const nav = useNavigate();
  const loc = useLocation();
  const bottomRef = useRef(null);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open, loading]);

  const ask = async (question) => {
    const q = (question || "").trim();
    if (!q || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }]);
    setLoading(true);
    try {
      const { data } = await api.post("/assistant/query", {
        question: q,
        session_id: sessionId,
        context: { path: loc.pathname },
      });
      setMessages((m) => [...m, {
        role: "assistant",
        text: data.answer || "Non lo trovo nei dati disponibili.",
        source: data.source || "",
        kind: data.kind || "info",
        actions: data.actions || [],
        ambiguous: data.ambiguous || [],
      }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: apiErr(e, "Errore assistente: riprova"), error: true, actions: [], ambiguous: [] }]);
    } finally { setLoading(false); }
  };

  const runAction = (a) => {
    if (a.type === "confirm_action" || a.type === "cancel_action") {
      if (a.action_id && usedActionIds.has(a.action_id)) return;
      setLoading(true);
      const ep = a.type === "confirm_action" ? "/assistant/action/confirm" : "/assistant/action/cancel";
      api.post(ep, { action_id: a.action_id, session_id: sessionId })
        .then(({ data }) => {
          if (a.action_id) setUsedActionIds((s) => new Set(s).add(a.action_id));
          setMessages((m) => [...m, {
            role: "assistant", text: data.answer || "Fatto.", source: data.source || "",
            kind: data.kind || "done", actions: data.actions || [], ambiguous: data.ambiguous || [],
          }]);
        })
        .catch((e) => setMessages((m) => [...m, { role: "assistant", text: apiErr(e, "Errore azione"), error: true, actions: [], ambiguous: [] }]))
        .finally(() => setLoading(false));
      return;
    }
    if (a.type === "prefill_appointment") {
      try { window.localStorage.setItem("pv-appt-prefill", JSON.stringify(a.payload?.patient || {})); } catch { /* noop */ }
      const qs = new URLSearchParams({ prefill: "1" });
      if (a.payload?.date) qs.set("date", a.payload.date);
      nav(`/agenda?${qs.toString()}`);
      setOpen(false);
    } else if (a.route) {
      nav(a.route);
      setOpen(false);
    } else {
      toast.info("Azione non disponibile");
    }
  };

  const actionBtnCls = (a) => {
    if (a.type === "confirm_action") return "bg-emerald-600 hover:bg-emerald-700 text-white";
    if (a.type === "cancel_action") return "bg-white border border-slate-300 text-slate-600 hover:bg-slate-100";
    return "bg-brand-navy hover:bg-brand-navyDark text-white";
  };

  return (
    <>
      {/* Pulsante flottante sempre raggiungibile */}
      <button
        data-testid="assistant-open-btn"
        onClick={() => setOpen((x) => !x)}
        title="Assistente IA"
        className="fixed bottom-5 right-5 z-[60] w-14 h-14 rounded-full bg-brand-navy hover:bg-brand-navyDark text-white shadow-xl flex items-center justify-center transition-transform hover:scale-105"
      >
        {open ? <XIcon size={22} /> : <Bot size={24} />}
      </button>

      {open && (
        <div
          data-testid="assistant-panel"
          className="fixed bottom-24 right-5 z-[60] w-[380px] max-w-[calc(100vw-2rem)] h-[540px] max-h-[calc(100vh-8rem)] bg-white rounded-2xl shadow-2xl border border-[#E2E8F0] flex flex-col overflow-hidden"
        >
          <div className="px-4 py-3 bg-brand-navy text-white flex items-center gap-2 shrink-0">
            <Bot size={18} />
            <div className="flex-1">
              <div className="font-extrabold text-sm leading-tight">Assistente IA</div>
              <div className="text-[10px] opacity-80">Risponde solo con i tuoi dati: pazienti, visite, agenda, tag, consensi</div>
            </div>
            <button data-testid="assistant-close-btn" onClick={() => setOpen(false)} className="p-1 rounded hover:bg-white/10"><XIcon size={16} /></button>
          </div>

          <div className="flex-1 overflow-y-auto overscroll-contain px-3 py-3 space-y-3 bg-slate-50" data-testid="assistant-messages">
            {messages.length === 0 && (
              <div className="text-center pt-4" data-testid="assistant-empty-state">
                <Sparkles className="mx-auto text-brand-azure mb-2" size={22} />
                <div className="text-xs text-slate-500 mb-3">Prova a chiedermi:</div>
                <div className="space-y-1.5 px-2">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} data-testid="assistant-suggestion" onClick={() => (s.endsWith("…") ? setInput(s.replace("…", " ")) : ask(s))}
                      className="w-full text-left text-[11px] font-semibold text-brand-navy bg-white border border-[#E2E8F0] hover:border-brand-azure rounded-lg px-3 py-2 transition">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[88%] rounded-xl px-3 py-2 text-[12.5px] leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-brand-azure text-white" : m.error ? "bg-red-50 border border-red-200 text-red-800" : "bg-white border border-[#E2E8F0] text-slate-800"}`}
                  data-testid={m.role === "user" ? "assistant-msg-user" : "assistant-msg-bot"}>
                  {m.role === "assistant" && (m.source || m.kind === "proposal") && (
                    <div className="mb-1.5 flex items-center gap-1.5">
                      {m.source && <SourceBadge source={m.source} />}
                      {m.kind === "proposal" && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800" data-testid="assistant-proposal-badge">Richiesta conferma</span>
                      )}
                      {m.kind === "done" && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-emerald-100 text-emerald-800" data-testid="assistant-done-badge">Azione completata</span>
                      )}
                    </div>
                  )}
                  {m.text}
                  {m.role === "assistant" && (m.ambiguous || []).length > 0 && (
                    <div className="mt-2 space-y-1" data-testid="assistant-ambiguous">
                      {m.ambiguous.map((c, j) => (
                        <button key={j} onClick={() => ask(c.question)} data-testid="assistant-ambiguous-option"
                          className="w-full text-left text-[11px] font-bold text-brand-navy bg-brand-azure/10 hover:bg-brand-azure/20 rounded-lg px-2.5 py-1.5">
                          {c.label}
                        </button>
                      ))}
                    </div>
                  )}
                  {m.role === "assistant" && (m.actions || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid="assistant-actions">
                      {m.actions.map((a, j) => {
                        const used = a.action_id && usedActionIds.has(a.action_id);
                        return (
                          <button key={j} onClick={() => runAction(a)} data-testid="assistant-action-btn" disabled={used}
                            className={`text-[11px] font-bold rounded-full px-3 py-1.5 ${actionBtnCls(a)} ${used ? "opacity-40 pointer-events-none" : ""}`}>
                            {a.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start" data-testid="assistant-loading">
                <div className="bg-white border border-[#E2E8F0] rounded-xl px-3 py-2 text-xs text-slate-500 flex items-center gap-2">
                  <Loader2 size={13} className="animate-spin" /> Cerco nei tuoi dati…
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <div className="p-3 border-t border-[#E2E8F0] bg-white shrink-0">
            <div className="flex gap-2">
              <input
                data-testid="assistant-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && ask(input)}
                placeholder="Chiedi qualcosa su pazienti, visite o agenda…"
                className="flex-1 px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm focus:outline-none focus:border-brand-azure"
                disabled={loading}
              />
              <button data-testid="assistant-send-btn" onClick={() => ask(input)} disabled={loading || !input.trim()}
                className="bg-brand-orange hover:bg-brand-orangeDark text-white font-bold px-3 py-2 rounded-lg disabled:opacity-40">
                <Send size={15} />
              </button>
            </div>
            <div className="text-[9px] text-slate-400 mt-1.5">Sola lettura: non modifica mai i dati clinici. Le azioni aprono pagine che confermi tu.</div>
          </div>
        </div>
      )}
    </>
  );
}
